import {
  type EnvironmentId,
  isProviderDriverKind,
  type OrchestrationThreadActivity,
  ProjectId,
  type ModelSelection,
  type ProviderDriverKind,
  type ServerProvider,
  type ScopedProjectRef,
  type ScopedThreadRef,
  type ThreadId,
  type TurnId,
} from "@amoreg/contracts";
import type { EnvironmentProject } from "@amoreg/client-runtime/state/shell";
import {
  type ChatMessage,
  DEFAULT_INTERACTION_MODE,
  type SessionPhase,
  type Thread,
  type ThreadShell,
} from "../types";
import type { WorkLogEntry } from "../session-logic";
import { resolveNamedToolPresentation } from "./chat/toolPresentation";
import {
  type ComposerImageAttachment,
  type ComposerThreadDraftState,
  type DraftThreadState,
} from "../composerDraftStore";
import * as Schema from "effect/Schema";
import { resolveThreadDetailRef } from "../state/entities";
import {
  filterTerminalContextsWithText,
  stripInlineTerminalContextPlaceholders,
  type TerminalContextDraft,
} from "../lib/terminalContext";
import type { DraftThreadEnvMode } from "../composerDraftStore";

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "amoreg:last-invoked-script-by-project";
export const MAX_HIDDEN_MOUNTED_TERMINAL_THREADS = 10;
export const MAX_HIDDEN_MOUNTED_PREVIEW_THREADS = 3;

/**
 * The routed thread whose pagination window drives "load earlier turns".
 * Draft routes and sent drafts still waiting for their server shell get no
 * ref: subscribing would poll the snapshot endpoint for a thread the server
 * does not have yet (404, then backoff polling every ~30 s; AMOREG-EVE-J).
 * The gate matches `useThread`'s `waitForShell`, so both subscribe together.
 */
export function resolveRoutePaginationThreadRef(
  routeKind: "server" | "draft",
  routeThreadRef: ScopedThreadRef,
  options: { shellExists: boolean; waitForShell: boolean },
): ScopedThreadRef | null {
  return routeKind === "server" ? resolveThreadDetailRef(routeThreadRef, options) : null;
}

export const ENVIRONMENT_RECONNECT_WARNING_GRACE_MS = 2_000;

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export function scheduleEnvironmentReconnectWarning(showWarning: () => void): () => void {
  const timeoutId = globalThis.setTimeout(showWarning, ENVIRONMENT_RECONNECT_WARNING_GRACE_MS);
  return () => globalThis.clearTimeout(timeoutId);
}

export function hasEnvironmentReconnectWarningGraceElapsed(
  activeEnvironmentId: EnvironmentId | null,
  elapsedEnvironmentId: EnvironmentId | null,
): boolean {
  return activeEnvironmentId !== null && activeEnvironmentId === elapsedEnvironmentId;
}

export function startNewThreadForProject(
  projectRef: ScopedProjectRef | null,
  // The shared handler resolves to the draft it opened; this caller ignores it.
  handleNewThread: (projectRef: ScopedProjectRef) => Promise<unknown>,
): boolean {
  if (projectRef === null) return false;
  void handleNewThread(projectRef);

  return true;
}

export function resolveThreadMetadataUpdateForNextTurn(input: {
  currentModelSelection: ModelSelection;
  nextModelSelection?: ModelSelection;
  currentBranch: string | null;
  nextBranch?: string;
}): {
  modelSelection?: ModelSelection;
  branch?: string;
  worktreePath?: null;
} | null {
  const nextModelSelection = input.nextModelSelection;
  const modelSelectionChanged =
    nextModelSelection !== undefined &&
    (nextModelSelection.model !== input.currentModelSelection.model ||
      nextModelSelection.instanceId !== input.currentModelSelection.instanceId ||
      JSON.stringify(nextModelSelection.options ?? null) !==
        JSON.stringify(input.currentModelSelection.options ?? null));
  const branchChanged = input.nextBranch !== undefined && input.nextBranch !== input.currentBranch;
  if (!modelSelectionChanged && !branchChanged) {
    return null;
  }
  return {
    ...(modelSelectionChanged ? { modelSelection: nextModelSelection } : {}),
    ...(branchChanged ? { branch: input.nextBranch, worktreePath: null } : {}),
  };
}

/**
 * Re-resolves a persisted draft's project when its stored ref no longer
 * matches any live project id. Drafts outlive server restarts and data-shape
 * changes, and older records stored the project's workspace root where the
 * id now lives — a miss then dead-locked the composer on "Choose a project"
 * with no way out short of discarding the draft. Matching by workspace root
 * (same environment) recovers the renamed project; the caller writes the
 * recovered ref back to the draft store.
 */
export function recoverDraftProjectRef(
  projects: ReadonlyArray<EnvironmentProject>,
  environmentId: EnvironmentId,
  staleProjectId: string,
): ScopedProjectRef | null {
  if (staleProjectId.length === 0) {
    return null;
  }
  const recovered = projects.find(
    (project) =>
      project.environmentId === environmentId &&
      (project.id === staleProjectId || project.workspaceRoot === staleProjectId),
  );
  return recovered ? { environmentId: recovered.environmentId, projectId: recovered.id } : null;
}

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
): Thread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    projectId: draftThread.projectId,
    title: "New thread",
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: DEFAULT_INTERACTION_MODE,
    session: null,
    messages: [],
    createdAt: draftThread.createdAt,
    updatedAt: draftThread.createdAt,
    archivedAt: null,
    settledOverride: null,
    settledAt: null,
    deletedAt: null,
    latestTurn: null,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    checkpoints: [],
    activities: [],
    proposedPlans: [],
  };
}

export function buildLoadingThreadFromShell(shell: ThreadShell): Thread {
  return {
    ...shell,
    messages: [],
    proposedPlans: [],
    activities: [],
    checkpoints: [],
    deletedAt: null,
  };
}

export function shouldWriteThreadErrorToCurrentServerThread(input: {
  activeServerThread:
    | {
        environmentId: EnvironmentId;
        id: ThreadId;
      }
    | null
    | undefined;
  routeThreadRef: ScopedThreadRef;
  targetThreadId: ThreadId;
}): boolean {
  return Boolean(
    input.activeServerThread &&
    input.targetThreadId === input.routeThreadRef.threadId &&
    input.activeServerThread.environmentId === input.routeThreadRef.environmentId &&
    input.activeServerThread.id === input.targetThreadId,
  );
}

export function buildThreadTurnInterruptInput(thread: Pick<Thread, "id" | "session">): {
  threadId: ThreadId;
  turnId?: TurnId;
} {
  const runningTurnId = thread.session?.status === "running" ? thread.session.activeTurnId : null;
  return {
    threadId: thread.id,
    ...(runningTurnId !== null ? { turnId: runningTurnId } : {}),
  };
}

export function reconcileMountedTerminalThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadTerminalOpen: boolean;
  maxHiddenThreadCount?: number;
}): string[] {
  return reconcileRetainedMountedThreadIds({
    currentThreadIds: input.currentThreadIds,
    openThreadIds: input.openThreadIds,
    activeThreadId: input.activeThreadId,
    activeThreadOpen: input.activeThreadTerminalOpen,
    maxHiddenThreadCount: input.maxHiddenThreadCount ?? MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  });
}

export function reconcileRetainedMountedThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadOpen: boolean;
  maxHiddenThreadCount: number;
  retainInactiveActiveThread?: boolean;
}): string[] {
  const openThreadIdSet = new Set(input.openThreadIds);
  const hiddenThreadIds = input.currentThreadIds.filter(
    (threadId) =>
      (threadId !== input.activeThreadId || input.retainInactiveActiveThread === true) &&
      openThreadIdSet.has(threadId),
  );
  const maxHiddenThreadCount = Math.max(0, input.maxHiddenThreadCount);
  const nextThreadIds =
    hiddenThreadIds.length > maxHiddenThreadCount
      ? hiddenThreadIds.slice(-maxHiddenThreadCount)
      : hiddenThreadIds;

  if (
    input.activeThreadId &&
    input.activeThreadOpen &&
    !nextThreadIds.includes(input.activeThreadId)
  ) {
    nextThreadIds.push(input.activeThreadId);
  }

  return nextThreadIds;
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

export { readFileAsDataUrl } from "../lib/fileDataUrl";

export function resolveSendEnvMode(input: {
  requestedEnvMode: DraftThreadEnvMode;
  isGitRepo: boolean;
}): DraftThreadEnvMode {
  return input.isGitRepo ? input.requestedEnvMode : "local";
}

export function cloneComposerImageForRetry(
  image: ComposerImageAttachment,
): ComposerImageAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: URL.createObjectURL(image.file),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
  /**
   * Optional element-pick attachment count. Element contexts contribute to
   * "sendable content" exactly like images and (text-bearing) terminal
   * contexts do: a prompt of just element chips is still a valid send.
   */
  elementContextCount?: number;
}): {
  trimmedPrompt: string;
  sendableTerminalContexts: TerminalContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineTerminalContextPlaceholders(options.prompt).trim();
  const sendableTerminalContexts = filterTerminalContextsWithText(options.terminalContexts);
  const expiredTerminalContextCount =
    options.terminalContexts.length - sendableTerminalContexts.length;
  const elementContextCount = options.elementContextCount ?? 0;
  return {
    trimmedPrompt,
    sendableTerminalContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 ||
      options.imageCount > 0 ||
      sendableTerminalContexts.length > 0 ||
      elementContextCount > 0,
  };
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function branchMismatchKey(
  threadId: string | null,
  mismatch: { threadBranch: string; currentBranch: string } | null,
): string | null {
  if (!threadId || !mismatch) {
    return null;
  }
  return `${threadId}:${mismatch.threadBranch}:${mismatch.currentBranch}`;
}

// The mismatch banner only matters when the user is about to send: passive
// reading of an old thread carries no risk (the branch picker tint already
// covers ambient awareness). Draft content is the intent signal — composer
// focus is useless here because ChatView autofocuses the composer on every
// thread open. `wasShownForCurrentMismatch` keeps the banner mounted once
// revealed so it doesn't flicker away when the draft is cleared.
export function shouldShowBranchMismatchBanner(input: {
  hasMismatch: boolean;
  isDismissed: boolean;
  composerHasContent: boolean;
  wasShownForCurrentMismatch: boolean;
}): boolean {
  if (!input.hasMismatch || input.isDismissed) {
    return false;
  }
  return input.composerHasContent || input.wasShownForCurrentMismatch;
}

// Session-scoped (module-level so it survives ChatView remounts, e.g. route
// changes). Durable cross-device dismissal is planned as a server-side ack.
const sessionDismissedBranchMismatchKeys = new Set<string>();

export function dismissBranchMismatchForSession(key: string): void {
  sessionDismissedBranchMismatchKeys.add(key);
}

export function isBranchMismatchDismissedForSession(key: string | null): boolean {
  return key !== null && sessionDismissedBranchMismatchKeys.has(key);
}

export function threadHasStarted(thread: Thread | null | undefined): boolean {
  return Boolean(
    thread && (thread.latestTurn !== null || thread.messages.length > 0 || thread.session !== null),
  );
}

// `threadProvider` is the open branded driver kind carried by the session.
// Unknown driver kinds degrade to `null` (i.e. "unlocked"), which is the safe
// rollback / fork behavior — the routing layer is the right place to surface
// "driver not installed" errors, not the lock state.
//
// `selectedProvider` takes the same open-string shape because the composer
// now tracks the picker selection as a `ProviderInstanceId` (e.g.
// `codex_personal`). Custom instance ids that don't directly match a
// registered driver resolve to `null` here, which matches the existing
// "unknown driver -> unlocked" semantics. Callers that want the lock to track
// a custom instance's underlying driver kind should resolve the instance id
// upstream and pass the correlated kind.
export function deriveLockedProvider(input: {
  thread: Thread | null | undefined;
  selectedProvider: string | null;
  threadProvider: string | null;
}): ProviderDriverKind | null {
  if (!threadHasStarted(input.thread)) {
    return null;
  }
  const sessionProvider = input.thread?.session?.providerName ?? null;
  if (sessionProvider && isProviderDriverKind(sessionProvider)) {
    return sessionProvider;
  }
  const narrowedThreadProvider =
    input.threadProvider && isProviderDriverKind(input.threadProvider)
      ? input.threadProvider
      : null;
  const narrowedSelectedProvider =
    input.selectedProvider && isProviderDriverKind(input.selectedProvider)
      ? input.selectedProvider
      : null;
  return narrowedThreadProvider ?? narrowedSelectedProvider ?? null;
}

export function getStartedThreadModelChangeBlockReason(input: {
  providers: ReadonlyArray<Pick<ServerProvider, "instanceId" | "requiresNewThreadForModelChange">>;
  hasStartedSession: boolean;
  currentModelSelection: ModelSelection;
  currentProviderInstanceId?: ModelSelection["instanceId"] | null | undefined;
  nextModelSelection: ModelSelection;
}): { title: string; description: string } | null {
  if (!input.hasStartedSession) {
    return null;
  }
  const currentModelSelection = {
    ...input.currentModelSelection,
    instanceId: input.currentProviderInstanceId ?? input.currentModelSelection.instanceId,
  };
  if (
    currentModelSelection.instanceId === input.nextModelSelection.instanceId &&
    currentModelSelection.model === input.nextModelSelection.model
  ) {
    return null;
  }
  const currentProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === currentModelSelection.instanceId,
  );
  const nextProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === input.nextModelSelection.instanceId,
  );
  if (
    currentProvider?.requiresNewThreadForModelChange !== true &&
    nextProvider?.requiresNewThreadForModelChange !== true
  ) {
    return null;
  }
  return {
    title: "Start a new chat to change models",
    description: "This provider does not allow switching models after a conversation has started.",
  };
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  latestUserMessageId: ChatMessage["id"] | null;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionStatus: NonNullable<Thread["session"]>["status"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  const latestUserMessage = activeThread?.messages.findLast((message) => message.role === "user");
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    latestUserMessageId: latestUserMessage?.id ?? null,
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionStatus: session?.status ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  latestUserMessageId: ChatMessage["id"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (input.hasPendingApproval || input.hasPendingUserInput || Boolean(input.threadError)) {
    return true;
  }
  if (input.phase === "connecting") {
    return false;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestUserMessageChanged =
    input.localDispatch.latestUserMessageId !== input.latestUserMessageId;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (input.phase === "running") {
    // Steering adds a user message to the current running turn without
    // necessarily changing any of the turn timestamps. Treat that projected
    // message as the server acknowledgment so the composer does not remain
    // stuck in its local "Sending" state until the turn settles.
    if (latestUserMessageChanged) {
      return true;
    }
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== null &&
      session?.activeTurnId !== undefined &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    input.localDispatch.sessionStatus !== (session?.status ?? null) ||
    input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}

type FocusedElement = Pick<Element, "tagName" | "closest" | "getAttribute"> & { isContentEditable?: boolean };

const isTextEntry = (element: FocusedElement) =>
  element.tagName === "INPUT" ||
  element.tagName === "TEXTAREA" ||
  element.tagName === "SELECT" ||
  element.isContentEditable === true ||
  element.getAttribute("role") === "textbox";

// Returning to the window should land the caret in the composer, so the reader can type right
// away. The exceptions are places where focus is deliberate: another text field, a terminal in
// the drawer or the right panel, or an open dialog or popup. A focused button outside those is
// not one of them, so it yields to the composer.
export function shouldRefocusComposerOnWindowFocus(activeElement: FocusedElement | null): boolean {
  if (activeElement === null || activeElement.tagName === "BODY") return true;
  if (isTextEntry(activeElement) || activeElement.tagName === "IFRAME" || activeElement.tagName === "WEBVIEW") {
    return false;
  }
  return (
    activeElement.closest(
      '[role="dialog"], [role="alertdialog"], [data-slot$="-popup"], [data-terminal-owner]',
    ) === null
  );
}

// Opening a chat lands the caret in its composer, unless the reader is already typing in another
// field: renaming the chat in the sidebar with a double-click opens it on the first click, and the
// rename must keep its focus.
export function shouldFocusComposerOnThreadOpen(activeElement: FocusedElement | null): boolean {
  if (activeElement === null || !isTextEntry(activeElement)) return true;
  return activeElement.closest('[data-testid="composer-editor"]') !== null;
}

export function buildRunningThreadTurnInterruptInput(
  thread: Pick<Thread, "id" | "session"> | null | undefined,
  phase: SessionPhase,
): { threadId: ThreadId; turnId?: TurnId } | null {
  if (phase !== "running" || thread?.session?.status !== "running") {
    return null;
  }
  return buildThreadTurnInterruptInput(thread);
}

/** The text a standalone manual-compaction request is sent as. */
export const COMPACT_COMMAND_TEXT = "/compact";

/** A user message that is only `/compact`: a request to compact, not a prompt. */
export function isCompactCommandMessage(
  message: Pick<ChatMessage, "role" | "text" | "attachments">,
): boolean {
  return (
    message.role === "user" &&
    (message.attachments?.length ?? 0) === 0 &&
    message.text.trim().toLowerCase() === COMPACT_COMMAND_TEXT
  );
}

/** The message the composer's Resume button sends after an interrupted reply. */
export const RESUME_THREAD_TEXT = "Continue where you left off.";

/**
 * With an empty composer, the send button becomes Resume when the latest reply
 * was interrupted and nothing runs. Adapted from pingdotgg/t3code (de3439142);
 * upstream also resumes usage-limit failures and held server queues, which
 * Amoreg does not have.
 */
export function canResumeInterruptedTurn(input: {
  isServerThread: boolean;
  latestTurn: Pick<NonNullable<Thread["latestTurn"]>, "state"> | null;
  phase: SessionPhase;
}): boolean {
  return (
    input.isServerThread && input.phase !== "running" && input.latestTurn?.state === "interrupted"
  );
}

const COMPACTION_PASS_KINDS = new Set([
  "context-compaction.started",
  "context-compaction.settled",
  "context-compaction",
]);
const MANUAL_COMPACTION_SETTLED_KINDS = new Set([
  "context-compaction.settled",
  "context-compaction",
  "provider.turn.start.failed",
]);

/**
 * Whether the thread's context is being compacted right now, which swaps the
 * working row's "Working for…" for "Compacting…".
 *
 * Two ways in: the running turn has a compaction pass open (eve compacting on
 * its own before a model call, or the pass a `/compact` opened), or the
 * latest message is a `/compact` the provider has not answered yet — which
 * covers the moment between sending it and the pass starting. Nothing counts
 * once the thread stops working.
 */
export function deriveIsCompacting(input: {
  readonly working: boolean;
  readonly activeTurnId: TurnId | null;
  readonly activities: ReadonlyArray<
    Pick<OrchestrationThreadActivity, "kind" | "turnId" | "createdAt">
  >;
  readonly latestUserMessage:
    | Pick<ChatMessage, "role" | "text" | "attachments" | "createdAt">
    | null
    | undefined;
}): boolean {
  if (!input.working) return false;
  const latestPass = input.activities.findLast((activity) =>
    COMPACTION_PASS_KINDS.has(activity.kind),
  );
  if (
    latestPass?.kind === "context-compaction.started" &&
    input.activeTurnId !== null &&
    latestPass.turnId === input.activeTurnId
  ) {
    return true;
  }
  const request = input.latestUserMessage;
  if (!request || !isCompactCommandMessage(request)) return false;
  return !input.activities.some(
    (activity) =>
      MANUAL_COMPACTION_SETTLED_KINDS.has(activity.kind) && activity.createdAt >= request.createdAt,
  );
}

/**
 * Nothing typed or attached in the composer, judged from both the live refs
 * and the stored draft: the composer refreshes its refs from the store only
 * in a passive effect after it commits, so either can be ahead of the other.
 */
export function isComposerDraftEmpty(
  live: {
    readonly prompt: string;
    readonly images: ReadonlyArray<unknown>;
    readonly terminalContexts: ReadonlyArray<unknown>;
    readonly elementContexts: ReadonlyArray<unknown>;
  },
  draft: Pick<
    ComposerThreadDraftState,
    | "prompt"
    | "images"
    | "files"
    | "terminalContexts"
    | "elementContexts"
    | "previewAnnotations"
    | "reviewComments"
  > | null,
): boolean {
  return (
    live.prompt.length === 0 &&
    live.images.length === 0 &&
    live.terminalContexts.length === 0 &&
    live.elementContexts.length === 0 &&
    (draft?.prompt.length ?? 0) === 0 &&
    (draft?.images.length ?? 0) === 0 &&
    (draft?.files.length ?? 0) === 0 &&
    (draft?.terminalContexts.length ?? 0) === 0 &&
    (draft?.elementContexts.length ?? 0) === 0 &&
    (draft?.previewAnnotations.length ?? 0) === 0 &&
    (draft?.reviewComments.length ?? 0) === 0
  );
}

/**
 * What the agent last did with this thread's browser: the open tabs plus the
 * latest browser tool call (and its lifecycle status). It changes when the
 * agent opens a browser, and also when it wakes a dormant one by reusing the
 * same tab and URL, which the tab list alone cannot show.
 */
export function cloudBrowserActivityKey(input: {
  readonly tabIds: ReadonlyArray<string>;
  readonly entries: ReadonlyArray<
    Pick<WorkLogEntry, "id" | "toolCallId" | "toolLifecycleStatus" | "toolName" | "toolTitle" | "label" | "itemType" | "toolData">
  >;
}): string {
  let call = "";
  for (let index = input.entries.length - 1; index >= 0; index -= 1) {
    const entry = input.entries[index]!;
    if (resolveNamedToolPresentation(entry)?.action === "browser") {
      call = `${entry.toolCallId ?? entry.id}:${entry.toolLifecycleStatus ?? ""}`;
      break;
    }
  }
  return `${[...input.tabIds].sort().join(",")}|${call}`;
}

/**
 * The cloud browser snapshot (`useBrowserPreview`) is fetched once per thread,
 * so a browser the agent opens or wakes mid-chat is never noticed and the
 * mini-player auto-open never fires. Watch for its live view whenever the
 * agent's browser activity changed since the thread loaded and no live view
 * is known. Native (desktop) previews do not need it. Neither the snapshot's
 * `dormant` nor its `available` flag is consulted: both are as stale as the
 * live view. A new chat's first snapshot is fetched while it is still a local
 * draft, which the server answers 404 ("unavailable"), and waking a dormant
 * browser is exactly the case to catch.
 */
export function shouldWatchForCloudLiveView(input: {
  readonly nativePreview: boolean;
  /** The running session's key (any provider); null when none is known. */
  readonly liveSession: string | null;
  readonly browserActivity: string;
  readonly browserActivityAtLoad: string;
}): boolean {
  return (
    !input.nativePreview &&
    input.liveSession === null &&
    input.browserActivity !== input.browserActivityAtLoad
  );
}

/**
 * The agent's latest round of browser use: the turn of its latest browser
 * call (the call itself when it has no turn), or null when it has made none.
 * One round pops the browser up at most once, however many calls it makes.
 */
export function cloudBrowserRound(
  entries: ReadonlyArray<
    Pick<
      WorkLogEntry,
      "id" | "turnId" | "toolCallId" | "toolName" | "toolTitle" | "label" | "itemType" | "toolData"
    >
  >,
): string | null {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]!;
    if (resolveNamedToolPresentation(entry)?.action === "browser") {
      return entry.turnId ?? entry.toolCallId ?? entry.id;
    }
  }
  return null;
}

/** What the auto-open last acted on: a browser session, in a round of the agent's browser use. */
export interface CloudBrowserAutoOpenMark {
  readonly liveSession: string;
  readonly round: string | null;
}

/**
 * Whether to float the cloud browser over the chat now. It opens once per
 * live view and once per round of the agent's browser use (AMO-231), as soon
 * as both the live view and the browser tab are known, whichever arrives
 * last. Closing it hides it until the agent uses the browser in a new round
 * (or a new session starts); within the round it stays closed. A session or
 * round first seen while the panel or the mini player is already showing
 * counts as handled.
 */
export function resolveCloudBrowserAutoOpen(input: {
  readonly nativePreview: boolean;
  /** The running session's key (any provider); a new key is a new session. */
  readonly liveSession: string | null;
  /** The agent's latest round of browser use (cloudBrowserRound). */
  readonly round: string | null;
  readonly handled: CloudBrowserAutoOpenMark | null;
  readonly panelOpen: boolean;
  readonly miniPlayerOpen: boolean;
  readonly activeTabId: string | null;
}): "open" | "handled" | "wait" {
  if (input.nativePreview || input.liveSession === null) return "wait";
  if (input.handled?.liveSession === input.liveSession && input.handled.round === input.round) {
    return "wait";
  }
  if (input.panelOpen || input.miniPlayerOpen) return "handled";
  return input.activeTabId === null ? "wait" : "open";
}

/**
 * Whether to read the browser snapshot again, once: the agent used the
 * browser since the chat loaded and a session is known. The known one may be
 * stale (the browser slept and the agent woke a new session), and the new
 * key is what lets the auto-open fire again. Without a known session the
 * bounded watch (shouldWatchForCloudLiveView) does this.
 */
export function shouldRecheckCloudLiveView(input: {
  readonly nativePreview: boolean;
  readonly liveSession: string | null;
  readonly browserActivity: string;
  readonly browserActivityAtLoad: string;
}): boolean {
  return (
    !input.nativePreview &&
    input.liveSession !== null &&
    input.browserActivity !== input.browserActivityAtLoad
  );
}

/** The server announces the tab just before it stores the live view, so one re-check can miss it. */
export const CLOUD_LIVE_VIEW_POLL_MS = 1_000;
export const CLOUD_LIVE_VIEW_POLL_ATTEMPTS = 15;

/**
 * Re-fetch the snapshot now and then once a second, a bounded number of
 * times. The caller cancels (the returned function) as soon as the live view
 * arrives or the inputs change.
 */
export function watchForCloudLiveView(refresh: () => void): () => void {
  refresh();
  let attempts = 0;
  const id = setInterval(() => {
    attempts += 1;
    refresh();
    if (attempts >= CLOUD_LIVE_VIEW_POLL_ATTEMPTS) clearInterval(id);
  }, CLOUD_LIVE_VIEW_POLL_MS);
  return () => clearInterval(id);
}
